import { Box, styled, Tooltip, type SxProps, type Theme } from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';

import { CloudProviders } from '@/assets/logos/cloudProviders';
import type { BackupVault } from '@/data/vaults/backupVault';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';
import { NativeVaultColor } from '@/theme/colorSchemes';

import { BackupVaultCard, VaultCardAsAccount } from './backupVaultCard';

const Tag = styled('div')(
  (props: { backgroundColor: string; additionalStyles?: object }) => ({
    cursor: 'default',
    borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
    fontSize: '12px',
    padding: '2px 8px',
    margin: '2px 4px',
    color: 'black',
    backgroundColor: props.backgroundColor,
    display: 'inline-block',
    ...props.additionalStyles,
  })
);

function VaultTagFromId({
  vaultId,
  showCard,
}: {
  vaultId: string;
  showCard?: boolean;
}) {
  const { vaults } = useBackupVaults();
  const vault = vaults.find((vault) => vault.id === vaultId);
  if (!vault) {
    return null;
  }

  return <VaultTag vault={vault} showCard={showCard} />;
}

export function VaultTag({
  sx,
  vault,
  /** The hover card is unwanted where the tag is itself a choice being pointed at. */
  showCard = true,
}: {
  sx?: SxProps<Theme>;
  vault: BackupVault | string;
  showCard?: boolean;
}) {
  if (typeof vault === 'string') {
    return <VaultTagFromId vaultId={vault} showCard={showCard} />;
  }

  return <BackupVaultTag sx={sx} vault={vault} showCard={showCard} />;
}

export const AccountAsVaultTag = ({
  sx,
  cloudProvider,
  region,
}: {
  sx?: SxProps<Theme>;
  cloudProvider: Provider;
  region: string;
}) => {
  const accountLabel =
    cloudProvider === Provider.Azure
      ? 'subscription'
      : cloudProvider === Provider.Gcp
        ? 'project'
        : 'account';

  return (
    <VaultTagInner
      sx={sx}
      backgroundColor={NativeVaultColor}
      name={`Source ${accountLabel}`}
      cloudProvider={cloudProvider}
      vaultCard={
        <VaultCardAsAccount
          sx={sx}
          cloudProvider={cloudProvider}
          region={region}
        />
      }
    />
  );
};

const BackupVaultTag = ({
  sx,
  vault,
  showCard,
}: {
  sx?: SxProps<Theme>;
  vault: BackupVault;
  showCard?: boolean;
}) => {
  return (
    <VaultTagInner
      sx={sx}
      backgroundColor={vault.backgroundColor || 'black'}
      name={vault.name}
      cloudProvider={vault.vaultAttributes.cloudProvider}
      vaultCard={
        showCard ? <BackupVaultCard data={vault} editable={false} /> : null
      }
    />
  );
};

const VaultTagInner = ({
  sx,
  backgroundColor,
  name,
  cloudProvider,
  vaultCard,
}: {
  sx?: SxProps<Theme>;
  backgroundColor: string;
  name: string;
  cloudProvider: string;
  vaultCard: React.ReactNode;
}) => {
  const tag = (
    <Tag
      data-testid='vault-tag'
      additionalStyles={{
        ...sx,
        maxWidth: '140px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
      backgroundColor={backgroundColor || 'black'}
    >
      <Box component={'span'} className='font-semibold mr-[4px]'>
        {CloudProviders[cloudProvider]?.displayName}
      </Box>
      <Box component={'span'}>{name}</Box>
    </Tag>
  );

  if (!vaultCard) {
    return tag;
  }

  return (
    <Tooltip
      disableInteractive={false}
      disableFocusListener
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                altAxis: true,
                padding: 8,
              },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['top', 'bottom', 'left', 'right'],
              },
            },
          ],
        },
        tooltip: {
          sx: {
            padding: '0px',
            backgroundColor: 'transparent !important',
            boxShadow: 'none',
            maxWidth: '300px',
          },
        },
      }}
      title={vaultCard}
    >
      {tag}
    </Tooltip>
  );
};
